<template>
  <div class="bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden group border border-gray-100 relative h-full">
    <!-- 상태 배지 -->
    <div class="absolute top-4 right-4">
      <span
        class="inline-flex px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wider shadow-sm"
        :class="{
          'bg-green-200 text-green-800': status === 'available',
          'bg-red-200 text-red-800': status === 'full',
          'bg-blue-200 text-blue-800': status === 'upcoming',
          'bg-gray-200 text-gray-500': status === 'closed'
        }"
      >
        {{ statusText }}
      </span>
    </div>

    <div class="p-6 pt-12 flex flex-col h-full">
      <div class="flex-1">
        <!-- 제목 & 장소 -->
        <RouterLink :to="`/posts/${post.id}`" class="block">
          <h3
            class="text-xl font-semibold text-gray-700 group-hover:text-blue-600 transition-colors line-clamp-2 min-h-[3rem]"
          >
            {{ post.title }}
          </h3>
        </RouterLink>
          <div class="flex items-center text-sm text-gray-500 mb-2">
            <MapPinIcon class="w-4 h-4 mr-1 opacity-70" />
            <span class="truncate">{{ post.place }}</span>
          </div>
        <p v-if="post.content" class="text-gray-600 text-sm line-clamp-2">{{ post.content }}</p>
      </div>

      <!-- 세부 정보 + 좌석 미리보기 -->
      <div class="flex gap-4 mt-4 pt-4 border-t border-gray-100">
        <!-- 왼쪽: 세부 정보 -->
        <div class="flex-1 space-y-2 min-w-0">
          <div class="flex items-start text-sm min-w-0">
            <CalendarDaysIcon class="w-4 h-4 mr-2 text-blue-400 flex-shrink-0 mt-0.5" />
            <div class="min-w-0 leading-5">
              <div class="text-gray-600 truncate" :title="formatDateTime(post.startDate)">
                시작 {{ formatDateTime(post.startDate) }}
              </div>
              <div class="text-gray-500 truncate" :title="formatDateTime(post.endDate)">
                종료 {{ formatDateTime(post.endDate) }}
              </div>
            </div>
          </div>
          <div class="flex items-center text-sm min-w-0">
            <UserIcon class="w-4 h-4 mr-2 text-blue-400 flex-shrink-0" />
            <span class="text-gray-600 truncate">{{ userDisplayName }}</span>
          </div>
          <div class="flex items-center text-sm min-w-0">
            <UserGroupIcon class="w-4 h-4 mr-2 text-blue-400 flex-shrink-0" />
            <span
              class="truncate"
              :class="{
                'text-green-600 font-medium': status === 'available',
                'text-red-600 font-medium': status === 'full',
                'text-blue-600 font-medium': status === 'upcoming',
                'text-gray-400 font-medium': status === 'closed'
              }"
            >
              {{ availabilityText }}
            </span>
          </div>
        </div>

        <!-- 오른쪽: 좌석 미리보기 -->
        <div class="flex flex-col items-end justify-center gap-2">
          <button
            type="button"
            class="hidden sm:flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2 hover:border-blue-200 hover:bg-blue-50 transition"
            @click="showSeatPreview = true"
          >
            <SeatLayoutMini :layout="seatLayoutInfo" :reserved-seat-numbers="reservedSeatNumbers" />
          </button>
          <button
            type="button"
            class="sm:hidden text-xs text-gray-500 hover:text-blue-600"
            @click="showSeatPreview = true"
          >
            좌석 보기
          </button>
        </div>
      </div>

      <!-- 액션 버튼 -->
      <div class="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
        <RouterLink
          :to="`/posts/${post.id}`"
          class="btn btn-outline btn-sm text-gray-500 hover:text-blue-600"
        >
          자세히 보기
        </RouterLink>

        <button
          v-if="status === 'available'"
          @click="$router.push(`/posts/${post.id}`)"
          class="btn btn-primary btn-sm bg-blue-600 hover:bg-blue-700 text-white flex items-center"
        >
          <ClockIcon class="w-4 h-4 mr-1" />
          예약하기
        </button>

        <span v-else-if="status === 'upcoming'" class="text-sm text-blue-600">
          예약 전
        </span>

        <span v-else-if="status === 'closed'" class="text-sm text-gray-500">
          예약 마감
        </span>

        <span v-else class="text-sm text-red-600">
          만석
        </span>
      </div>
    </div>

    <SeatPreviewModal
      :show="showSeatPreview"
      :title="post.title"
      :layout="seatLayoutInfo"
      :reserved-seat-numbers="reservedSeatNumbers"
      @close="showSeatPreview = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import {
  MapPinIcon,
  CalendarDaysIcon,
  UserIcon,
  UserGroupIcon,
  ClockIcon
} from '@heroicons/vue/24/outline'
import type { Post, SeatLayoutInfo } from '@/types'
import SeatLayoutMini from '@/components/SeatLayoutMini.vue'
import SeatPreviewModal from '@/components/SeatPreviewModal.vue'

interface Props {
  post: Post
  seatLayoutInfo: SeatLayoutInfo
  reservedSeatNumbers?: number[] | undefined
}

const props = withDefaults(defineProps<Props>(), {
  reservedSeatNumbers: undefined
})

const showSeatPreview = ref(false)
// 카드 상태 계산
const totalSeatCount = computed(() => {
  const postTotal = props.post.totalSeats ?? 0
  if (postTotal > 0) return postTotal
  return props.seatLayoutInfo?.totalSeats ?? 0
})

const reservedSeatCount = computed(() => {
  if (props.reservedSeatNumbers !== undefined) {
    return props.reservedSeatNumbers.length
  }
  return props.post.reservedSeats ?? 0
})

const status = computed(() => {
  const now = new Date()
  const startDate = new Date(props.post.startDate)
  const endDate = new Date(props.post.endDate)
  const hasAvailableSeats = totalSeatCount.value > reservedSeatCount.value

  if (now < startDate) return 'upcoming'
  if (now > endDate) return 'closed'
  return hasAvailableSeats ? 'available' : 'full'
})

const statusText = computed(() => {
  switch (status.value) {
    case 'upcoming':
      return '예약전'
    case 'closed':
      return '예약마감'
    case 'full':
      return '만석'
    default:
      return '예약가능'
  }
})

const availabilityText = computed(() => {
  const availableSeats = totalSeatCount.value - reservedSeatCount.value

  if (status.value === 'upcoming') return '예약 시작 전'
  if (status.value === 'closed') return `${reservedSeatCount.value} / ${totalSeatCount.value} 예약완료`
  if (status.value === 'full') return '만석'
  return `${availableSeats}석 남음 (전체 ${totalSeatCount.value}석)`
})

const userDisplayName = computed(() => {
  return props.post.userName || props.post.user?.name || ''
})

// 날짜 포맷
const formatDateTime = (dateString: string) => {
  const date = new Date(dateString)
  const datePart = `${date.getFullYear().toString().slice(2)}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
  const timePart = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  return `${datePart} ${timePart}`
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
